import React from 'react';
import { Users, RefreshCw } from 'lucide-react';

const HorizontalPatientQueue = ({ 
  patientQueue, 
  queueStats, 
  loading, 
  selectedPatient, 
  onPatientSelect, 
  onStartService, 
  onCompleteService, 
  onRefresh 
}) => {
  const calculateWaitingTime = (queueJoinedAt) => {
    if (!queueJoinedAt) return 'N/A';
    
    const now = new Date();
    const joinedTime = new Date(queueJoinedAt);
    const diffInMinutes = Math.floor((now - joinedTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just joined';
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    } else {
      const hours = Math.floor(diffInMinutes / 60);
      const minutes = diffInMinutes % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  };

  // Removed unused getStatusColor and PatientPhoto to simplify component

  const handleStartService = (appointmentId) => {
    onStartService(appointmentId);
  };

  const handleCompleteService = (appointmentId) => {
    onCompleteService(appointmentId);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" style={{ width: '100%', maxWidth: '100vw' }}>
      {/* Compact Header */}
      <div className="px-3 py-1.5 border-b bg-gray-50 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="h-5 w-5 text-[#185b6b]" />
          <span className="text-xl font-medium text-black">Patient Queue</span>
          <span className="text-xl text-black">({patientQueue.length})</span>
        </div>
        <button 
          onClick={onRefresh}
          disabled={loading}
          className="p-1 text-gray-500 hover:text-[#2D869C] transition-colors"
          title="Refresh Queue"
        >
          <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      <div className="p-1.5" style={{ maxWidth: '100%', overflow: 'hidden' }}>
        {loading ? (
          <div className="flex items-center justify-center py-1.5">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2D869C] mr-2"></div>
            <div className="text-gray-600 text-xs">Loading...</div>
          </div>
        ) : patientQueue.length === 0 ? (
          <div className="flex items-center justify-center py-1.5">
            <Users className="h-5 w-5 text-gray-300 mr-2" />
            <div className="text-xs text-gray-500">No patients in queue</div>
          </div>
        ) : (
          <div 
            className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" 
            style={{ 
              width: '100%', 
              maxWidth: '100%'
            }}
          >
            <div className="flex space-x-3 pb-1.5 pr-2" style={{ width: 'max-content', minWidth: '100%' }}>
            {patientQueue.map((appointment, index) => {
              const fullName = `${appointment.patient?.firstName || 'Unknown'} ${appointment.patient?.lastName || ''}`.trim();
              return (
              <div 
                key={appointment._id}
                className={`flex-shrink-0 border rounded-xl p-2.5 cursor-pointer transition-all duration-200 shadow-sm ${
                  selectedPatient?._id === appointment._id 
                    ? 'border-[#2D869C] bg-[#9DC9D4]/20 shadow-md ring-2 ring-[#9DC9D4]' 
                    : 'border-gray-200 hover:border-[#2D869C] bg-white hover:bg-gray-50 hover:shadow-md'
                }`}
                style={{ width: '340px', minWidth: '340px', maxWidth: '340px' }}
                onClick={() => onPatientSelect(appointment)}
              >
                {/* Compact Card Layout */}
                <div className="flex items-center space-x-3 h-full min-h-[150px]">
                  {/* Left: Compact Patient Photo */}
                  <div className="relative flex-shrink-0 h-32 w-32 rounded-xl overflow-hidden border-2 border-[#9DC9D4] shadow-sm bg-[#9DC9D4]/20 flex items-center justify-center">
                    {appointment.patient?.photo?.url ? (
                      <img
                        src={appointment.patient.photo.url}
                        alt={`${appointment.patient.firstName || 'Patient'} photo`}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          // Show fallback initials if image load fails
                          const fallback = e.target.parentElement.querySelector('[data-fallback-initials]');
                          if (fallback) fallback.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div
                      data-fallback-initials
                      className={`${appointment.patient?.photo?.url ? 'hidden' : 'flex'} absolute inset-0 flex items-center justify-center text-white text-3xl font-bold bg-[#2D869C]`}
                    >
                      {(appointment.patient?.firstName || 'U').charAt(0).toUpperCase()}
                    </div>
                  </div>

                  {/* Right: Compact Info and CTA */}
                  <div className="flex-1 flex flex-col justify-between h-32">
                    <div className="min-w-0">
                      <h3
                        className="font-semibold text-gray-900 text-xl overflow-hidden break-words leading-tight"
                        style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}
                        title={fullName}
                      >
                        {fullName}
                      </h3>
                      <p className="text-base font-semibold text-gray-500">waiting time</p>
                      <p
                        className={`text-lg font-semibold ${
                          appointment.status === 'InProgress'
                            ? 'text-green-600'
                            : calculateWaitingTime(appointment.queueJoinedAt).includes('h')
                              ? 'text-red-500'
                              : 'text-amber-600'
                        }`}
                      >
                        {appointment.status === 'InProgress' ? 'In Service' : calculateWaitingTime(appointment.queueJoinedAt)}
                      </p>
                    </div>

                    <div className="flex justify-end">
                      {appointment.status === 'InQueue' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onPatientSelect(appointment);
                            handleStartService(appointment._id);
                          }}
                          className="bg-[#2D869C] hover:bg-[#236b7a] text-white text-base py-2.5 px-5 rounded-lg transition-colors font-medium"
                        >
                          start
                        </button>
                      )}

                      {appointment.status === 'InProgress' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompleteService(appointment._id);
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white text-base py-2.5 px-5 rounded-lg transition-colors font-medium"
                        >
                          complete
                        </button>
                      )}

                      {appointment.status === 'Completed' && (
                        <div className="bg-gray-100 text-gray-600 text-base py-2.5 px-5 rounded-lg text-center font-medium">
                          ✓ done
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HorizontalPatientQueue;